import { Loader2, Inbox, Check } from "lucide-react";
import { Card } from "./ui/card";
import { label, fmtDateTime, money } from "../lib/api";

const STATUS = {
  green: ["approved", "active", "paid", "verified", "funded", "completed", "dismissed", "assigned", "activated"],
  yellow: ["opened", "submitted", "under_review", "requested", "pending", "pending_approval", "changes_requested", "scheduled", "reserved", "unpaid", "credit_approved", "pending_review", "medium"],
  red: ["unsubscribed", "rejected", "suspended", "failed", "expired", "high", "confirmed", "closed", "void", "open"],
  blue: ["in_progress", "processing", "draft", "paused", "cancelled", "archived", "not_submitted", "unfunded", "low"],
};
const TONE = {
  green: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20",
  yellow: "bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-500/20",
  red: "bg-red-500/10 text-red-600 dark:text-red-400 ring-red-500/20",
  blue: "bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-blue-500/20",
};

export function StatusBadge({ status, testid }) {
  const tone = Object.keys(STATUS).find((k) => STATUS[k].includes(status)) || "blue";
  return (
    <span data-testid={testid || `status-${status}`} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset whitespace-nowrap ${TONE[tone]}`}>
      {label(status)}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ title, value, icon: Icon, hint, tone = "default", testid }) {
  const accent = { default: "text-primary bg-primary/10", green: "text-emerald-500 bg-emerald-500/10", yellow: "text-amber-500 bg-amber-500/10", red: "text-red-500 bg-red-500/10" }[tone];
  return (
    <Card className="p-5 rounded-xl hover:shadow-md transition-shadow duration-300" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</p>
        {Icon && <span className={`rounded-lg p-2 ${accent}`}><Icon className="h-4 w-4" /></span>}
      </div>
      <p className="mt-3 font-mono text-2xl font-semibold tabular-nums" data-testid={testid ? `${testid}-value` : undefined}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </Card>
  );
}

export function Money({ cents, className = "", testid }) {
  return <span data-testid={testid} className={`font-mono tabular-nums ${className}`}>{money(cents)}</span>;
}

export function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" data-testid="page-loader">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", text, action, icon: Icon = Inbox }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6 rounded-xl border border-dashed" data-testid="empty-state">
      <span className="rounded-full bg-primary/10 p-3 text-primary mb-4"><Icon className="h-5 w-5" /></span>
      <p className="font-semibold">{title}</p>
      {text && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{text}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorBox({ error }) {
  if (!error) return null;
  return <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400" data-testid="error-box">{error}</div>;
}

export function DataTable({ columns, rows, testid, empty = "No records found", onRowClick, selected, onSelect, canSelect = () => true }) {
  if (!rows?.length) return <EmptyState title={empty} />;
  if (onSelect) {
    const ids = rows.filter(canSelect).map((r) => r.id);
    const all = ids.length > 0 && ids.every((i) => selected.includes(i));
    const box = (checked, on, tid) => <input type="checkbox" className="h-4 w-4 accent-primary cursor-pointer" checked={checked} onChange={on} onClick={(e) => e.stopPropagation()} data-testid={tid} />;
    columns = [{ key: "__sel", className: "w-10", label: box(all, () => onSelect(all ? selected.filter((i) => !ids.includes(i)) : [...new Set([...selected, ...ids])]), `${testid}-select-all`),
      render: (r) => canSelect(r) ? box(selected.includes(r.id), () => onSelect(selected.includes(r.id) ? selected.filter((i) => i !== r.id) : [...selected, r.id]), `${testid}-select-${r.id}`) : null }, ...columns];
  }
  return (
    <div className="overflow-x-auto rounded-xl border bg-card" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-muted/50 sticky top-0">
          <tr>{columns.map((c) => <th key={c.key} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap ${c.className || ""}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} onClick={onRowClick ? () => onRowClick(r) : undefined} data-testid={`${testid}-row-${i}`}
              className={`border-t transition-colors hover:bg-muted/40 ${onRowClick ? "cursor-pointer" : ""} ${selected?.includes(r.id) ? "bg-primary/5" : ""}`}>
              {columns.map((c) => <td key={c.key} className={`px-4 py-3 align-middle ${c.className || ""}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Timeline({ items = [] }) {
  return (
    <ol className="relative border-l border-border ml-2 space-y-5" data-testid="timeline">
      {items.map((t, i) => (
        <li key={i} className="ml-5">
          <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-background ${i === items.length - 1 ? "bg-primary" : "bg-muted-foreground/40"}`}>
            {i < items.length - 1 && <Check className="h-2.5 w-2.5 text-white" />}
          </span>
          <div className="flex flex-wrap items-center gap-2"><StatusBadge status={t.status} testid={`timeline-${i}`} /><span className="text-xs text-muted-foreground">{fmtDateTime(t.at)}</span></div>
          {t.note && <p className="text-sm text-muted-foreground mt-1">{t.note}</p>}
        </li>
      ))}
    </ol>
  );
}

export function Section({ title, actions, children, className = "", testid }) {
  return (
    <Card className={`rounded-xl p-5 ${className}`} data-testid={testid}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 mb-4">
          {title && <h3 className="font-display text-base font-semibold">{title}</h3>}
          {actions}
        </div>
      )}
      {children}
    </Card>
  );
}
